import { Controller } from "@hotwired/stimulus"

// Controller pour la signature rapide (quick sign)
// Gère la modal de confirmation avec focus trap WCAG 2.2
export default class extends Controller {
  static targets = ["modal", "form", "timestamp", "previewImage"]

  static values = {
    sessionModule: String,
    sessionDate: String,
    sessionTime: String,
    sessionTrainer: String
  }

  connect() {
    this.previouslyFocusedElement = null
    this.timestampInterval = null

    // Stocker la référence AVANT de déplacer vers body
    // car Stimulus perd le lien après le déplacement
    if (this.hasModalTarget) {
      this.modal = this.modalTarget
      this.timestampEl = this.hasTimestampTarget ? this.timestampTarget : null

      // Déplacer la modal vers le body pour échapper au stacking context
      // créé par backdrop-filter du parent (.liquid-glass-card)
      document.body.appendChild(this.modal)

      // Ajouter les event listeners manuellement car data-action ne fonctionne
      // plus après le déplacement hors du scope du controller
      this.setupModalEventListeners()
    }
  }

  setupModalEventListeners() {
    // Backdrop click pour fermer
    const backdrop = this.modal.querySelector("[data-modal-backdrop]")
    if (backdrop) {
      backdrop.addEventListener("click", () => this.closeModal())
    }

    // Boutons de fermeture
    const closeButtons = this.modal.querySelectorAll("[data-modal-close]")
    closeButtons.forEach(button => {
      button.addEventListener("click", (e) => {
        e.preventDefault()
        this.closeModal()
      })
    })

    // Escape key
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)
  }

  disconnect() {
    this.stopTimestampUpdate()
    if (this.modal && !this.modal.classList.contains("hidden")) {
      this.closeModal()
    }

    // Retirer l'event listener global
    if (this.boundHandleKeydown) {
      document.removeEventListener("keydown", this.boundHandleKeydown)
    }

    // Nettoyer : retirer la modal du body
    if (this.modal && this.modal.parentElement === document.body) {
      this.modal.remove()
    }
  }

  openConfirmation(event) {
    event.preventDefault()

    if (!this.modal) return

    this.previouslyFocusedElement = document.activeElement
    this.modal.classList.remove("hidden")
    this.modal.setAttribute("aria-hidden", "false")
    document.body.style.overflow = "hidden"

    this.startTimestampUpdate()
    this.setupFocusTrap()
    this.focusFirstElement()
  }

  close(event) {
    event?.preventDefault()
    this.closeModal()
  }

  closeModal() {
    if (!this.modal) return

    if (this.boundTrapFocus) {
      this.modal.removeEventListener("keydown", this.boundTrapFocus)
    }
    this.modal.classList.add("hidden")
    this.modal.setAttribute("aria-hidden", "true")
    document.body.style.overflow = ""

    this.stopTimestampUpdate()

    if (this.previouslyFocusedElement && this.previouslyFocusedElement.focus) {
      this.previouslyFocusedElement.focus()
      this.previouslyFocusedElement = null
    }
  }

  closeOnBackdrop(event) {
    // Ne rien faire - le backdrop a sa propre action
  }

  preventPropagation(event) {
    event.stopPropagation()
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.modal && !this.modal.classList.contains("hidden")) {
      this.close(event)
    }
  }

  startTimestampUpdate() {
    this.updateTimestamp()
    this.timestampInterval = setInterval(() => this.updateTimestamp(), 1000)
  }

  stopTimestampUpdate() {
    if (this.timestampInterval) {
      clearInterval(this.timestampInterval)
      this.timestampInterval = null
    }
  }

  updateTimestamp() {
    if (!this.timestampEl) return

    const now = new Date()
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }
    this.timestampEl.textContent = now.toLocaleDateString("fr-FR", options)
  }

  setupFocusTrap() {
    if (!this.modal) return
    this.boundTrapFocus = this.trapFocus.bind(this)
    this.modal.addEventListener("keydown", this.boundTrapFocus)
  }

  trapFocus(event) {
    if (event.key !== "Tab") return

    const focusableElements = this.getFocusableElements()
    if (focusableElements.length === 0) return

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
      return
    }

    if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  getFocusableElements() {
    if (!this.modal) return []

    const focusableSelectors = [
      "a[href]",
      "button:not([disabled])",
      "textarea:not([disabled])",
      'input:not([type="hidden"]):not([disabled])',
      "select:not([disabled])",
      '[tabindex]:not([tabindex="-1"])'
    ].join(", ")

    const elements = Array.from(this.modal.querySelectorAll(focusableSelectors))

    return elements.filter(element => {
      return element.offsetWidth > 0 &&
             element.offsetHeight > 0 &&
             !element.hasAttribute("hidden") &&
             window.getComputedStyle(element).visibility !== "hidden"
    })
  }

  focusFirstElement() {
    const focusableElements = this.getFocusableElements()

    if (focusableElements.length > 0) {
      setTimeout(() => {
        focusableElements[0].focus()
      }, 100)
    }
  }
}
